/* AQUON Ingredient Cards 2 - premium 3D flip.
   Front image card flips to a frosted detail back. Cards flip independently.
   One disclosure controller for every device (mouse, keyboard, touch):
   - the front is a real <button> (aria-expanded + aria-controls -> back panel);
     click / Enter / Space toggles it, Escape or the close button closes it;
   - the closed back is aria-hidden + inert, the open front is inert, so the
     inactive face is out of the tab order and the accessibility tree;
   - focus moves to the close button once the back is visible (CSS swaps the
     faces when the card is edge-on) and returns to the front on close.
   The asset is included once per section instance: the IIFE runs once, roots
   are bound once (delegated listeners), and shopify:section:load is safe. */
(function () {
  'use strict';
  if (window.AquonIngredientCards2) return;

  var boundRoots = typeof WeakSet === 'function' ? new WeakSet() : null;
  var timers = typeof WeakMap === 'function' ? new WeakMap() : null;
  var reduceMQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var hoverMQ = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;

  function isBound(root) { return boundRoots ? boundRoots.has(root) : root.__aic2Bound === true; }
  function markBound(root) { if (boundRoots) boundRoots.add(root); else root.__aic2Bound = true; }
  function getTimer(card) { return timers ? timers.get(card) : card.__aic2Timer; }
  function setTimer(card, id) { if (timers) timers.set(card, id); else card.__aic2Timer = id; }

  function parts(card) {
    return {
      front: card.querySelector('[data-aic2-front]'),
      back: card.querySelector('[data-aic2-back]'),
      close: card.querySelector('[data-aic2-close]'),
      scroller: card.querySelector('.aic2__back-content')
    };
  }

  function setInert(el, on) {
    if (!el) return;
    if (on) el.setAttribute('inert', ''); else el.removeAttribute('inert');
  }

  function isOpen(card) { return card.classList.contains('is-flipped'); }

  // The CSS swaps face visibility when the card is edge-on (47% of the flip
  // duration); a face cannot take focus before it is visible. Reduced motion
  // swaps instantly.
  function swapDelay(card) {
    if (reduceMQ && reduceMQ.matches) return 0;
    var root = card.closest('[data-aic2-root]') || card;
    var raw = String(getComputedStyle(root).getPropertyValue('--aic2-flip-dur') || '').trim();
    var ms = parseFloat(raw);
    if (!isFinite(ms)) ms = 700;
    else if (/[^m]s$/.test(raw)) ms *= 1000;
    return Math.max(0, ms * 0.47) + 20;
  }

  // Focus once the element is visible; retry for a few frames in case the
  // visibility swap lands a frame late.
  function focusWhenVisible(el, tries, done, current) {
    if (!current()) return;
    if (el) { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }
    if (!el || document.activeElement === el || tries <= 0) { done(); return; }
    window.requestAnimationFrame(function () { focusWhenVisible(el, tries - 1, done, current); });
  }

  // Move focus only if it is still in this card (or nowhere), never steal it.
  function focusAllowed(card) {
    var a = document.activeElement;
    return !a || a === document.body || card.contains(a);
  }

  function setOpen(card, open, opts) {
    var p = parts(card);
    if (!p.front || !p.back) return;
    opts = opts || {};
    var pending = getTimer(card);
    if (pending) { clearTimeout(pending); setTimer(card, 0); }
    var seq = (card.__aic2Seq || 0) + 1;
    card.__aic2Seq = seq;
    function current() { return card.__aic2Seq === seq && isOpen(card) === open; }

    card.classList.toggle('is-flipped', open);
    p.front.setAttribute('aria-expanded', open ? 'true' : 'false');
    // The incoming face is released now; CSS keeps it visibility:hidden until
    // the midpoint, so it is neither seen nor reachable before it shows.
    if (open) { setInert(p.back, false); p.back.setAttribute('aria-hidden', 'false'); }
    else setInert(p.front, false);

    function retire() {
      if (!current()) return;
      if (open) {
        setInert(p.front, true);
      } else {
        if (p.scroller) { p.scroller.tabIndex = -1; p.scroller.scrollTop = 0; }
        setInert(p.back, true);
        p.back.setAttribute('aria-hidden', 'true');
      }
    }

    function finish() {
      setTimer(card, 0);
      if (!current()) return;
      if (open && p.scroller) p.scroller.tabIndex = p.scroller.scrollHeight > p.scroller.clientHeight + 1 ? 0 : -1;
      var target = open ? (p.close || p.scroller) : p.front;
      if (opts.focus !== false && focusAllowed(card)) focusWhenVisible(target, 10, retire, current);
      else retire();
    }

    var delay = opts.instant ? 0 : swapDelay(card);
    if (delay > 0) setTimer(card, setTimeout(finish, delay)); else finish();
  }

  function initCard(card) {
    var p = parts(card);
    if (!p.front || !p.back) return;
    var open = isOpen(card);
    p.front.setAttribute('aria-expanded', open ? 'true' : 'false');
    p.back.setAttribute('aria-hidden', open ? 'false' : 'true');
    setInert(p.back, !open);
    setInert(p.front, open);
    if (p.scroller && !open) p.scroller.tabIndex = -1;
    card.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse' || e.buttons || !hoverMQ || !hoverMQ.matches || isOpen(card) || card.contains(document.activeElement)) return;
      card.__aic2HoverTimer = setTimeout(function () {
        if (isOpen(card)) return;
        card.__aic2HoverOpen = true;
        setOpen(card, true, { focus: false });
      }, 120);
    });
    card.addEventListener('pointerdown', function () {
      if (card.__aic2HoverTimer) clearTimeout(card.__aic2HoverTimer);
    });
    card.addEventListener('pointerleave', function () {
      if (card.__aic2HoverTimer) clearTimeout(card.__aic2HoverTimer);
      if (card.__aic2HoverOpen && !card.contains(document.activeElement)) {
        card.__aic2HoverOpen = false;
        setOpen(card, false, { focus: false });
      }
    });
    card.addEventListener('focusout', function (e) {
      if (card.__aic2HoverOpen && !card.contains(e.relatedTarget) && !card.matches(':hover')) {
        card.__aic2HoverOpen = false;
        setOpen(card, false, { focus: false });
      }
    });
  }

  function cardFrom(target, selector, root) {
    if (!target || !target.closest) return null;
    var hit = target.closest(selector);
    if (!hit || !root.contains(hit)) return null;
    return hit.closest('[data-aic2-card]');
  }

  function bindRoot(root) {
    if (isBound(root)) return;
    markBound(root);
    var cards = root.querySelectorAll('[data-aic2-card]');
    for (var i = 0; i < cards.length; i++) initCard(cards[i]);

    // Buttons fire click for mouse, touch, Enter and Space alike.
    root.addEventListener('click', function (e) {
      var card = cardFrom(e.target, '[data-aic2-close]', root);
      if (card) {
        card.__aic2HoverOpen = false;
        setOpen(card, false, { focus: true });
        return;
      }
      card = cardFrom(e.target, '[data-aic2-front]', root);
      if (card) {
        if (card.__aic2HoverTimer) clearTimeout(card.__aic2HoverTimer);
        var open = card.__aic2HoverOpen || !isOpen(card);
        card.__aic2HoverOpen = false;
        setOpen(card, open, { focus: true });
      }
    });
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      var card = cardFrom(e.target, '[data-aic2-card]', root);
      if (!card || !isOpen(card)) return;
      e.stopPropagation();
      card.__aic2HoverOpen = false;
      setOpen(card, false, { focus: true });
    });
  }

  function initAll(scope) {
    var s = scope && scope.querySelectorAll ? scope : document;
    if (s.matches && s.matches('[data-aic2-root]')) bindRoot(s);
    var roots = s.querySelectorAll('[data-aic2-root]');
    for (var i = 0; i < roots.length; i++) bindRoot(roots[i]);
  }

  function editorCard(e) {
    var t = e.target;
    if (!t || !t.closest) return null;
    var card = t.closest('[data-aic2-card]');
    if (!card) return null;
    var root = card.closest('[data-aic2-root]');
    if (root) bindRoot(root);
    return card;
  }

  window.AquonIngredientCards2 = {
    init: initAll,
    open: function (card) { if (card) setOpen(card, true, { focus: false }); },
    close: function (card) { if (card) setOpen(card, false, { focus: false }); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  else initAll(document);

  // Theme editor: re-rendered sections are new nodes; bind them once.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  // Theme editor: show the selected block's detail face without moving focus.
  document.addEventListener('shopify:block:select', function (e) {
    var card = editorCard(e);
    if (card) setOpen(card, true, { focus: false, instant: true });
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var card = editorCard(e);
    if (card) setOpen(card, false, { focus: false, instant: true });
  });
})();
